import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  LibraryBooks as LibraryBooksIcon,
  AutoAwesome as AutoAwesomeIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import api from '../api';

const SOURCE_LABELS = {
  'codex-initial-2026-08-05': 'Codex 2026-08-05',
  codex: 'Codex 2026-08-05',
  'history-2025-2026': '历史报价',
  history: '历史报价',
};

// 单次渲染行数封顶：几百行 × 12 列 MUI 组件一次 commit 在手机上是秒级卡顿，
// 库还会随导入脚本持续增长；配合搜索使用，未命中的行不渲染
const MAX_RENDER_ROWS = 100;

export default function QuotaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  // ===== 三栏对比（历史锚点 / 市场参考 / 官方人工）=====
  const [benchQ, setBenchQ] = useState('');
  const [benchLoading, setBenchLoading] = useState(false);
  const [bench, setBench] = useState(null);

  const runBenchmark = async () => {
    const q = benchQ.trim();
    if (!q || benchLoading) return;
    try {
      setBenchLoading(true);
      setBench(null);
      const res = await api.get('/api/price-benchmark', { params: { q } });
      setBench(res.data);
    } catch (e) {
      setBench({ error: e.response?.data?.error || '查询失败' });
    } finally {
      setBenchLoading(false);
    }
  };

  // ===== 市场调研（AI 联网出区间+依据 → 人工采纳）=====
  const [isAdmin, setIsAdmin] = useState(false);
  const [mkName, setMkName] = useState('');
  const [mkUnit, setMkUnit] = useState('');
  const [mkTrade, setMkTrade] = useState('');
  const [researching, setResearching] = useState(false);
  const [mkError, setMkError] = useState(null);
  const [mkList, setMkList] = useState([]);
  const [mkStatus, setMkStatus] = useState('PENDING');

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      setIsAdmin(u.role === 'admin');
    } catch (e) { /* ignore */ }
    fetchMarketPrices();
  }, []);

  const fetchMarketPrices = async (status = mkStatus) => {
    try {
      const res = await api.get('/api/market-prices', { params: status ? { status } : {} });
      setMkList(res.data);
    } catch (e) { /* 静默：非管理员也能看已审核条目 */ }
  };

  const runResearch = async () => {
    if (!mkName.trim() || researching) return;
    try {
      setResearching(true);
      setMkError(null);
      const res = await api.post('/api/market-prices/research', {
        items: [{ name: mkName.trim(), unit: mkUnit.trim() || undefined, trade: mkTrade.trim() || undefined }],
      });
      setMkName(''); setMkUnit(''); setMkTrade('');
      setMkStatus('PENDING');
      fetchMarketPrices('PENDING');
      setMkError(null);
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } catch (e) {
      setMkError(e.response?.data?.error || `调研失败：${e.message}`);
    } finally {
      setResearching(false);
    }
  };

  const reviewMarket = async (id, status) => {
    try {
      await api.put(`/api/market-prices/${id}`, { status });
      fetchMarketPrices();
    } catch (e) {
      setMkError(e.response?.data?.error || '操作失败');
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/quota-items');
      setItems(response.data);
    } catch (e) {
      console.error('Error fetching quota items:', e);
      setError(e.response?.data?.error || e.message || '加载单价库失败');
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(
    () => [...new Set(items.map((i) => i.category).filter(Boolean))].sort(),
    [items]
  );

  const fmt = (v) => {
    const n = Number(v);
    return n > 0 ? n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
  };

  const range = (floor, ceiling) => {
    const f = Number(floor) || 0;
    const c = Number(ceiling) || 0;
    if (f <= 0 && c <= 0) return '-';
    if (c <= 0) return fmt(f);
    return `${f <= 0 ? '?' : f} ~ ${c}`;
  };

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return items.filter((it) => {
      if (category && it.category !== category) return false;
      if (!kw) return true;
      return [it.name, it.specification, it.quotaCode, it.projectName, it.notes]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(kw));
    });
  }, [items, search, category]);

  const stats = useMemo(() => {
    const hist = filtered.filter((i) => (i.source || '').startsWith('history')).length;
    return { total: filtered.length, hist, codex: filtered.length - hist };
  }, [filtered]);

  const visible = useMemo(() => filtered.slice(0, MAX_RENDER_ROWS), [filtered]);
  const capped = filtered.length > visible.length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>单价库</Typography>
        <Paper sx={{ p: 3 }}>
          <Typography color="error">{error}</Typography>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={fetchItems}>重新加载</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <LibraryBooksIcon color="primary" />
          <Typography variant="h4">单价库</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          共 {stats.total} 条（历史报价 {stats.hist} · Codex {stats.codex}）· 单位：元
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              label="搜索名称 / 规格 / 编码 / 来源工程"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 320 }}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>分类</InputLabel>
              <Select
                value={category}
                label="分类"
                onChange={(e) => setCategory(e.target.value)}
              >
                <MenuItem value="">全部分类</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(search || category) && (
              <Button
                variant="text"
                onClick={() => { setSearch(''); setCategory(''); }}
              >
                清除筛选
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              没有匹配的单价条目
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: '72vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>名称</TableCell>
                <TableCell>规格/说明</TableCell>
                <TableCell>分类</TableCell>
                <TableCell>单位</TableCell>
                <TableCell align="right">材料</TableCell>
                <TableCell align="right">人工</TableCell>
                <TableCell align="right">机械</TableCell>
                <TableCell align="right">综合价</TableCell>
                <TableCell align="right">成本区间</TableCell>
                <TableCell align="right">审计区间</TableCell>
                <TableCell>来源工程</TableCell>
                <TableCell>引用时间</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((it) => (
                <TableRow key={it.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{it.name}</Typography>
                    {it.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', maxWidth: 320, whiteSpace: 'normal' }}
                        title={it.notes}
                      >
                        {it.notes.length > 60 ? it.notes.slice(0, 60) + '…' : it.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{it.specification || '-'}</TableCell>
                  <TableCell>
                    <Chip label={it.category || '-'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{it.unit}</TableCell>
                  <TableCell align="right">{fmt(it.materialPrice)}</TableCell>
                  <TableCell align="right">{fmt(it.laborPrice)}</TableCell>
                  <TableCell align="right">{fmt(it.machineryPrice)}</TableCell>
                  <TableCell align="right">{fmt(it.comprehensivePrice)}</TableCell>
                  <TableCell align="right">{range(it.costFloor, it.costCeiling)}</TableCell>
                  <TableCell align="right">{range(it.auditFloor, it.auditCeiling)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{it.projectName || SOURCE_LABELS[it.source] || it.source || '-'}</Typography>
                    {it.projectName && (
                      <Chip
                        label={SOURCE_LABELS[it.source] || it.source || '历史'}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{it.quotedAt || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {capped && (
        <Alert severity="info" sx={{ mt: 2 }}>
          匹配 {stats.total} 条，仅渲染前 {MAX_RENDER_ROWS} 条——请用搜索/分类缩小范围查看其余条目。
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        取价提示：同名条目先看作业高度（常规/绳索）、再看含量（含不含拆除/外运/管件），小面积单价显著偏高属正常摊销；
        报价建议落在审计区间内，低于成本下限不投。建单时在明细行「从单价库带入」可直接引用。
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* ===== 三栏对比：历史成交 / 市场参考 / 官方人工 ===== */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <CompareArrowsIcon color="primary" />
            <Typography variant="h6">三栏对比（防闭门造车）</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            输入关键词，一次对比 <strong>历史成交锚点</strong> / <strong>市场参考价</strong> / <strong>官方定额人工</strong> 三层价格基准。
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <TextField
              label="关键词（如：SBS / 落水管 / 窗檐）"
              value={benchQ}
              onChange={(e) => setBenchQ(e.target.value)}
              size="small"
              sx={{ minWidth: 260 }}
              onKeyDown={(e) => { if (e.key === 'Enter') runBenchmark(); }}
            />
            <Button variant="contained" onClick={runBenchmark} disabled={benchLoading || !benchQ.trim()} startIcon={benchLoading ? <CircularProgress size={16} /> : <SearchIcon />}>
              {benchLoading ? '查询中…' : '对比'}
            </Button>
          </Box>
          {bench && !bench.error && (
            <Box sx={{ mt: 2 }}>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={2}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" color="primary">① 历史成交锚点（{bench.anchors.length}）</Typography>
                  {(bench.anchors || []).length === 0 && <Typography variant="caption" color="text.secondary">无匹配</Typography>}
                  {(bench.anchors || []).slice(0, 6).map((a, i) => (
                    <Typography key={i} variant="caption" display="block" noWrap title={`${a.name} ${a.specification || ''}`}>
                      · {a.name.slice(0, 18)} ¥{fmt(a.comprehensivePrice)}/{a.unit}
                    </Typography>
                  ))}
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" color="secondary">② 市场参考（已审 {bench.market.length}）</Typography>
                  {(bench.market || []).length === 0 && <Typography variant="caption" color="text.secondary">无——可在下方发起 AI 调研</Typography>}
                  {(bench.market || []).slice(0, 4).map((m, i) => (
                    <Typography key={i} variant="caption" display="block" noWrap title={m.basis || ''}>
                      · {m.name.slice(0, 16)} {fmt(m.priceLow)}~{fmt(m.priceHigh)}/{m.unit}
                    </Typography>
                  ))}
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">③ 官方定额人工</Typography>
                  {(bench.labors || []).length === 0 && <Typography variant="caption" color="text.secondary">无（晋建科字〔2022〕65号）</Typography>}
                  {(bench.labors || []).slice(0, 4).map((l, i) => (
                    <Typography key={i} variant="caption" display="block">
                      · {l.workType} ¥{fmt(l.price)}/{l.unit || '工日'}
                    </Typography>
                  ))}
                </Paper>
              </Box>
            </Box>
          )}
          {bench && bench.error && <Alert severity="error" sx={{ mt: 2 }}>{bench.error}</Alert>}
        </CardContent>
      </Card>

      {/* ===== 市场参考价：AI 联网调研 + 人工审核 ===== */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <AutoAwesomeIcon color="secondary" />
            <Typography variant="h6">市场参考价 · AI 调研</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            AI 联网搜索公开行情（政务信息价、电商、行业测评），结合咱的锚点和官方人工定额给出区间与依据；
            <strong> 产出先挂"待审"，管理员采纳后才进入三栏对比</strong>。调研耗时 1~2 分钟属正常。
          </Typography>
          {isAdmin && (
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
              <TextField label="材料/项目名" value={mkName} onChange={(e) => setMkName(e.target.value)} size="small" sx={{ minWidth: 200 }} />
              <TextField label="单位（可选）" value={mkUnit} onChange={(e) => setMkUnit(e.target.value)} size="small" sx={{ width: 110 }} />
              <TextField label="工种（可选）" value={mkTrade} onChange={(e) => setMkTrade(e.target.value)} size="small" sx={{ width: 110 }} />
              <Button variant="contained" color="secondary" onClick={runResearch} disabled={researching || !mkName.trim()} startIcon={researching ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}>
                {researching ? '联网调研中…（1-2分钟）' : 'AI 调研'}
              </Button>
            </Box>
          )}
          {mkError && <Alert severity="error" sx={{ mb: 2 }}>{mkError}</Alert>}
          <Box sx={{ mb: 1 }}>
            {['PENDING', 'APPROVED', 'REJECTED', ''].map((s) => (
              <Chip key={s || 'ALL'} label={s === 'PENDING' ? '待审' : s === 'APPROVED' ? '已采纳' : s === 'REJECTED' ? '已驳回' : '全部'}
                size="small" onClick={() => { setMkStatus(s); fetchMarketPrices(s); }}
                color={mkStatus === s ? 'primary' : 'default'} variant={mkStatus === s ? 'filled' : 'outlined'} sx={{ mr: 1 }} />
            ))}
          </Box>
          {mkList.length === 0 ? (
            <Typography variant="caption" color="text.secondary">该状态下暂无条目</Typography>
          ) : (
            mkList.map((m) => (
              <Paper key={m.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} alignItems="center">
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {m.name} {m.specification ? `（${m.specification}）` : ''}
                      <Chip size="small" sx={{ ml: 1 }} color={m.status === 'APPROVED' ? 'success' : m.status === 'REJECTED' ? 'default' : 'warning'}
                        variant="outlined" label={m.status === 'APPROVED' ? '已采纳' : m.status === 'REJECTED' ? '已驳回' : '待审'} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.trade ? `[${m.trade}] ` : ''}区间 <strong>{fmt(m.priceLow)} ~ {fmt(m.priceHigh)}</strong> 元/{m.unit} · 建议 <strong>{fmt(m.suggested)}</strong>
                    </Typography>
                  </Box>
                  {isAdmin && m.status === 'PENDING' && (
                    <Box>
                      <Button size="small" variant="contained" color="success" sx={{ mr: 1 }} onClick={() => reviewMarket(m.id, 'APPROVED')}>采纳</Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => reviewMarket(m.id, 'REJECTED')}>驳回</Button>
                    </Box>
                  )}
                </Box>
                {m.basis && <Typography variant="caption" display="block" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: 'text.secondary' }}>依据：{m.basis}</Typography>}
                {m.sources && <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>来源：{m.sources}</Typography>}
              </Paper>
            ))
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
